/**
 * The sim→render event bus. Contract: docs/EVENT_VOCABULARY.md (binding).
 *
 * Every event is a fixed-stride record, so a reader advances by EVENT_STRIDE
 * and never has to know the kind to find the next one. The sim writes; the
 * three consumers (animation, audio, time) only read.
 *
 * The whole point of routing feel through here is that it is the ONE place a
 * renderer is allowed to learn that something happened. A channel that infers
 * an impact from a position delta or from another channel's callback is a bug
 * against the contract (F5), because it can drift out of sync with the frame
 * the sim actually said it on.
 */
import { EVENT_STRIDE, type Ev } from './constants.js';
import type { RunState } from './state.js';

type EvKind = (typeof Ev)[keyof typeof Ev];

/**
 * Append one event. Unused payload slots are written as 0, never omitted —
 * a short record would shift every subsequent event by the difference and
 * silently reinterpret the whole rest of the tick.
 */
export function emit(
  s: RunState, kind: EvKind, subject: number,
  p0 = 0, p1 = 0, p2 = 0, p3 = 0, p4 = 0, p5 = 0,
): void {
  s.events.push(kind, subject, p0, p1, p2, p3, p4, p5);
}

/**
 * Unit direction from `(dx, dy)`, for the events that carry one.
 *
 * `sqrt` is deliberate and is the only normalisation permitted here: IEEE-754
 * specifies it as correctly rounded, so it is bit-identical across Node,
 * Chromium, Firefox and WebKit. `atan2`/`sin`/`cos` carry no such guarantee
 * and are the classic source of replay desync (Iron Rule 1, AUDIT A-03).
 *
 * A zero-length input yields (0, 0) rather than NaN — a body hit from exactly
 * its own position has no direction, and NaN would poison the hash.
 */
export function unitDir(dx: number, dy: number): [number, number] {
  const len2 = dx * dx + dy * dy;
  if (len2 <= 0) return [0, 0];
  const inv = 1 / Math.sqrt(len2);
  return [dx * inv, dy * inv];
}

/**
 * FNV-1a over the event buffer — a SEPARATE trail from the state hash.
 *
 * Kept separate on purpose. Folded together, a CI failure says only "something
 * diverged". Apart, the pair localises the fault immediately:
 *   state differs                  -> sim divergence
 *   state matches, events differ   -> emission divergence: the sim computed
 *                                     the same world and described it
 *                                     differently
 * Those have different causes and different fixes, and telling them apart is
 * the entire diagnostic value of hashing events at all.
 *
 * This is also what makes the contract self-verifying: without it, the one
 * interface every feel channel depends on is the only thing the cross-engine
 * check does not cover.
 */
export function hashEvents(s: RunState): number {
  let h = 0x811c9dc5;
  const e = s.events;
  for (let i = 0; i < e.length; i++) {
    // Same fold as hashState: quantise to 1/4096 and mix four bytes.
    const scaled = (e[i]! * 4096) | 0;
    h ^= scaled & 0xff; h = Math.imul(h, 0x01000193) >>> 0;
    h ^= (scaled >>> 8) & 0xff; h = Math.imul(h, 0x01000193) >>> 0;
    h ^= (scaled >>> 16) & 0xff; h = Math.imul(h, 0x01000193) >>> 0;
    h ^= (scaled >>> 24) & 0xff; h = Math.imul(h, 0x01000193) >>> 0;
  }
  // Length is mixed last so a buffer of all-zero events cannot collide with
  // an empty tick — a stride bug is exactly the kind of fault that produces
  // trailing zeros, and it must not hash the same as emitting nothing.
  h ^= (e.length / EVENT_STRIDE) & 0xff;
  return Math.imul(h, 0x01000193) >>> 0;
}
